import React, { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { DialogContent, DialogTitle, Typography } from '@material-ui/core'
import { Button } from 'sputnik-ui'
import { Formik, Form } from 'formik'
import styled from 'styled-components'
import FormFieldWithError from 'components/atoms/FormFieldWithError'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { createEmergency } from 'store/operations'
import StyledActionsDialog from 'components/atoms/StyledActionsDialog'
import StyledModalDialog from 'components/atoms/StyledModalDialog'
import Transition from 'components/atoms/Transition'
import ChooseEmergencyAddresses from '../ChooseEmergencyAddresses'

const StyledFormTypography = styled(Typography)`
  && {
    margin-top: 1rem;
    font-weight: 400;
  }
`

const styles = { field: { marginTop: '0.5rem' } }
const CreateEmergencyUnlockModal = ({
  open,
  handleClose,
  currentUser,
  ...props
}) => {
  const { t } = useTranslation(['common', 'emergency'])
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  const schema = Yup.object().shape({
    duration: Yup.number()
      .positive(t('common:field.error.number.positive'))
      .integer(t('common:field.error.number.int'))
      .required(t('common:field.error.required')),
    title: Yup.string().required(t('common:field.error.required')),
    addresses: Yup.object().test(
      'has-addresses',
      t('common:field.error.required'),
      (val) => val && Object.keys(val).length > 0
    ),
  })

  const [openChooseAddress, setOpenChooseAddress] = useState(false)

  const handleSubmit = useCallback(
    (values) => {
      setLoading(true)
      dispatch(
        createEmergency({
          title: values.title,
          address_uuids: JSON.stringify(values.addresses),
          duration: values.duration * 60,
          kind: 'unlock',
          door_open: values.openDoor,
        })
      ).then(handleClose)
    },
    [dispatch, handleClose]
  )
  return (
    <StyledModalDialog
      open={open}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
      onClose={handleClose}
      PaperProps={{
        style: { borderRadius: 20, maxHeight: '100vh' },
      }}
      {...props}
    >
      <DialogTitle>{t('emergency:create.modal_title_unlock')}</DialogTitle>

      <Formik
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{
          title: '',
          duration: '',
          addresses: {},
          openDoor: false,
        }}
      >
        {({ touched, errors, setFieldValue, values }) => {
          const handleAddressChange = (streetUuid, houseUuid) => {
            const newSelected = { ...values.addresses }
            if (newSelected[streetUuid] && !houseUuid) {
              delete newSelected[streetUuid]
              setFieldValue('addresses', newSelected)
              return
            }
            newSelected[streetUuid] = newSelected[streetUuid] || []
            if (houseUuid) {
              const housesSelected = [...newSelected[streetUuid]]
              const currentIndex = housesSelected.indexOf(houseUuid)
              if (currentIndex === -1) {
                housesSelected.push(houseUuid)
              } else {
                housesSelected.splice(currentIndex, 1)
              }
              newSelected[streetUuid] = housesSelected
            }
            setFieldValue('addresses', newSelected)
          }

          const selectAllAddresses = (uuids) => {
            if (
              Object.keys(uuids).length === Object.keys(values.addresses).length
            ) {
              setFieldValue('addresses', {})
            } else {
              setFieldValue('addresses', uuids)
            }
          }

          return (
            <Form>
              <DialogContent>
                <StyledFormTypography variant="h6">
                  {t('emergency:create.title_field')}
                </StyledFormTypography>
                <FormFieldWithError
                  fullWidth
                  name="title"
                  style={styles.field}
                  error={errors.title}
                  touched={touched.title}
                />
                <div style={{ ...styles.field, display: 'flex', gap: '1rem' }}>
                  <div>
                    <StyledFormTypography variant="h6">
                      {t('devices:units.intercom_plural')}
                    </StyledFormTypography>
                    <Typography color="textSecondary">
                      {`${t('emergency:create.intercom_count')}: ${
                        Object.keys(values.addresses).length || 0
                      }`}
                    </Typography>
                    {errors.addresses && (
                      <Typography color="error" variant="caption">
                        {errors.addresses}
                      </Typography>
                    )}
                  </div>
                  <Button
                    primary
                    variant="v2"
                    size="small"
                    style={{ marginTop: '1rem' }}
                    onClick={() => {
                      setOpenChooseAddress(true)
                    }}
                  >
                    {t('common:button.change')}
                  </Button>
                </div>

                <StyledFormTypography variant="h6">
                  {t('devices:video_archive.duration_date')}
                </StyledFormTypography>
                <FormFieldWithError
                  style={styles.field}
                  fullWidth
                  name="duration"
                  label={t('emergency:create.duration_desc')}
                  type="number"
                  error={errors.duration}
                  touched={touched.duration}
                />
              </DialogContent>

              <StyledActionsDialog>
                <Button
                  type="submit"
                  primary
                  variant="v2"
                  fullWidth
                  disabled={loading}
                  style={{ marginBottom: '1rem', width: '20rem' }}
                >
                  {t('common:button.connect')}
                </Button>
                <Button onClick={handleClose} tertiary>
                  {t('devices:video_archive.back')}
                </Button>
              </StyledActionsDialog>

              <ChooseEmergencyAddresses
                open={openChooseAddress}
                handleClose={() => setOpenChooseAddress(false)}
                handleAddressChange={handleAddressChange}
                choosenAddresses={values.addresses}
                selectAll={selectAllAddresses}
                onSuccess={() => {
                  setOpenChooseAddress(false)
                }}
              />
            </Form>
          )
        }}
      </Formik>
    </StyledModalDialog>
  )
}

export default CreateEmergencyUnlockModal
